import { Link } from "@tanstack/react-router";

import { PluginConnectionAction, PluginStateLabel } from "./PluginConnectionStatus";
import {
  PLUGIN_ROW_CLASS,
  PLUGIN_ROW_CONTROL_CLASS,
  PLUGIN_ROW_LINK_CLASS,
  PluginIconTile,
  PluginRowText,
} from "./pluginRowChrome";
import {
  pluginConnectionState,
  type ForgePlugin,
  type ForgePluginId,
  type PluginConnectionAttempt,
} from "./pluginsPage.logic";

interface PluginRowProps {
  readonly plugin: ForgePlugin;
  readonly attempt: PluginConnectionAttempt | null;
  readonly onConnect: (pluginId: ForgePluginId) => void;
  readonly onDisconnect?: ((pluginId: ForgePluginId) => void) | undefined;
  readonly isChecking?: boolean | undefined;
  readonly detailsLink?: boolean;
}

export function PluginRow({
  plugin,
  attempt,
  onConnect,
  onDisconnect,
  isChecking,
  detailsLink = true,
}: PluginRowProps) {
  const state = pluginConnectionState(plugin);
  // The label only earns its place when the action does not already say the
  // same thing. "Connected" is the menu trigger's own text and "Connect" is the
  // button's, so `connected` and `pending` both had a badge restating the
  // control beside them. `error` and `unknown` are the two states the action
  // cannot express: "Reconnect" does not distinguish a broken connection from
  // one that was never made, and an unknown state has no action text at all.
  const badge =
    state === "error" || state === "unknown" ? <PluginStateLabel state={state} /> : null;

  return (
    // The hover plane marks a navigable row. Where the row is not a link — the
    // first-account-setup panel passes `detailsLink={false}` — offering one
    // would advertise a destination that isn't there.
    <article
      className={
        detailsLink ? `${PLUGIN_ROW_CLASS} transition-colors hover:bg-accent` : PLUGIN_ROW_CLASS
      }
    >
      {detailsLink ? (
        // One link across both columns rather than a link on the icon and a
        // second on the name: two adjacent targets in a 48px row cannot both
        // carry a coarse-pointer hit area without overlapping, and the row is
        // one destination either way.
        <Link
          to="/plugins/$pluginId"
          params={{ pluginId: plugin.id }}
          className={PLUGIN_ROW_LINK_CLASS}
          aria-label={`Open ${plugin.name}`}
        >
          <PluginIconTile pluginId={plugin.id} />
          <PluginRowText name={plugin.name} description={plugin.description} badge={badge} />
        </Link>
      ) : (
        <>
          <PluginIconTile pluginId={plugin.id} />
          <PluginRowText name={plugin.name} description={plugin.description} badge={badge} />
        </>
      )}
      <span className={PLUGIN_ROW_CONTROL_CLASS}>
        <PluginConnectionAction
          plugin={plugin}
          attempt={attempt}
          isChecking={isChecking}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
        />
      </span>
    </article>
  );
}
